import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import AdminTopNavbar from './components/AdminTopNavbar';
import { getContactMessagesAdmin, deleteContactMessageAdmin } from '../api/apis';
import { getDashboardStats, getRecentActivity } from '../api/adminApi';
import { Users, DollarSign, TrendingUp, Activity, Calendar, Upload, Eye, Mail, MessageSquare, Tag, Trash2 } from 'lucide-react';
import '../styles/admin-unified.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalStreamers: 0,
    totalCards: 0,
    activeUsers: 0,
    activeStreamers: 0,
    activeCards: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recentActivity, setRecentActivity] = useState([]);
  const [dailyCards, setDailyCards] = useState([]);
  const [contactQueries, setContactQueries] = useState([]);
  const [loadingQueries, setLoadingQueries] = useState(false);

  // Helper: classify the purpose of a contact message based on subject/message keywords
  const classifyPurpose = (subject = '', message = '') => {
    const text = `${subject} ${message}`.toLowerCase();
    if (/bug|issue|error|problem|not working|crash/.test(text)) return 'Support';
    if (/price|pricing|plan|billing|payment|invoice|refund/.test(text)) return 'Billing';
    if (/feature|request|improve|idea|suggestion/.test(text)) return 'Feature';
    if (/partnership|partner|collab|business|sponsor/.test(text)) return 'Partnership';
    if (/account|login|signup|password|otp|verify/.test(text)) return 'Account';
    if (/hire|job|career|position|role/.test(text)) return 'Careers';
    return 'General';
  };

  // Helper: map purpose to badge colors
  const purposeBadgeClass = (purpose) => {
    switch (purpose) {
      case 'Support':
        return 'bg-red-500/10 text-red-300 border border-red-500/20';
      case 'Billing':
        return 'bg-purple-500/10 text-purple-300 border border-purple-500/20';
      case 'Feature':
        return 'bg-green-500/10 text-green-300 border border-green-500/20';
      case 'Partnership':
        return 'bg-pink-500/10 text-pink-300 border border-pink-500/20';
      case 'Account':
        return 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/20';
      case 'Careers':
        return 'bg-blue-500/10 text-blue-300 border border-blue-500/20';
      default:
        return 'bg-gray-500/10 text-gray-300 border border-gray-500/20';
    }
  };

  useEffect(() => {
    fetchDashboardStats();
    fetchRecentActivity();
    fetchDailyCards();
    fetchContactQueries();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const data = await getDashboardStats();
      setStats(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch dashboard stats');
      toast.error(err.message || 'Failed to fetch dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const data = await getRecentActivity();
      setRecentActivity(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch recent activity');
      toast.error(err.message || 'Failed to fetch recent activity');
    }
  };

  const fetchDailyCards = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/api/broadcast-cards/today`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await response.json();
      if (data.success) {
        const todaysCards = data.cards.slice(0, 3);
        // Ensure we always have exactly 3 cards for display
        const paddedCards = [...todaysCards];
        while (paddedCards.length < 3) {
          paddedCards.push({
            title: `Card ${paddedCards.length + 1}`,
            imageUrl: '',
            isPublished: false
          });
        }
        setDailyCards(paddedCards);
      }
    } catch (err) {
      console.error('Error fetching daily cards:', err);
    }
  };

  const fetchContactQueries = async () => {
    try {
      setLoadingQueries(true);
      const response = await getContactMessagesAdmin({
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });
      if (response.data.success) {
        setContactQueries(response.data.data.messages);
      }
    } catch (error) {
      console.error('Failed to fetch contact queries:', error);
    } finally {
      setLoadingQueries(false);
    }
  };

  const handleDeleteQuery = async (id) => {
    if (!window.confirm('Are you sure you want to delete this query?')) return;
    try {
      await deleteContactMessageAdmin(id, {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });
      toast.success('Query deleted successfully');
      fetchContactQueries();
    } catch (error) {
      console.error('Failed to delete query:', error);
      toast.error('Failed to delete query');
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <AdminTopNavbar />
        <div className="admin-loading">
          <div className="admin-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <AdminTopNavbar />

      <div className="admin-content">
        {/* Header */}
        <div className="admin-header">
          <h1 className="admin-title">Admin Dashboard</h1>
          <p className="admin-subtitle">Monitor and manage your platform performance</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Users */}
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Total Users</p>
                <p className="stat-number">{stats.totalUsers || 1}</p>
                <p className="stat-change positive">+12% from last month</p>
              </div>
              <div className="stat-icon">
                <Users className="w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Total Revenue */}
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Total Revenue</p>
                <p className="stat-number">₹{stats.totalRevenue || 0}</p>
                <p className="stat-change positive">+8% from last month</p>
              </div>
              <div className="stat-icon">
                <DollarSign className="w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Active Streams */}
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Active Streams</p>
                <p className="stat-number">{stats.activeStreams || 0}</p>
                <p className="stat-change negative">-3% from last month</p>
              </div>
              <div className="stat-icon">
                <Activity className="w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Total Cards */}
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Total Cards</p>
                <p className="stat-number">{stats.totalCards || 0}</p>
                <p className="stat-change positive">+15% from last month</p>
              </div>
              <div className="stat-icon">
                <TrendingUp className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity - Redesigned */}
        <div className="admin-card">
          <div className="border-b border-primary pb-4 mb-6 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-accent">Recent Activity</h2>
            <div className="flex gap-2">
              <button className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded-full transition-colors">
                All
              </button>
              <button className="text-xs bg-gray-800 hover:bg-gray-700 text-gray-400 px-3 py-1 rounded-full transition-colors">
                Users
              </button>
              <button className="text-xs bg-gray-800 hover:bg-gray-700 text-gray-400 px-3 py-1 rounded-full transition-colors">
                System
              </button>
            </div>
          </div>

          <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar space-y-3">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <div
                  key={index}
                  className="bg-gray-800/30 hover:bg-gray-800/60 border border-gray-700/30 rounded-lg p-4 transition-all duration-200 group cursor-pointer"
                  onClick={() => {
                    // Could open a modal here with full details
                    toast.info(`Activity details: ${activity.action}`);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`mt-1 w-8 h-8 rounded-full flex items-center justify-center ${activity.status === 'success' ? 'bg-green-500/10 text-green-400' :
                        activity.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400' :
                          'bg-gray-500/10 text-gray-400'
                        }`}>
                        <Activity className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{activity.action || 'No action'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {activity.user || 'Unknown User'}
                          </span>
                          <span className="text-gray-600 text-[10px]">•</span>
                          <span className="text-xs text-gray-500">{activity.time || 'Unknown time'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider ${activity.status === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
                        activity.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' :
                          'bg-gray-500/10 text-gray-400 border border-gray-500/20'
                        }`}>
                        {activity.status || 'Unknown'}
                      </span>
                      {activity.value && (
                        <span className="text-xs font-mono text-gray-400">{activity.value}</span>
                      )}
                    </div>
                  </div>

                  {/* Expandable details hint */}
                  <div className="mt-2 pt-2 border-t border-gray-700/30 hidden group-hover:block animate-fadeIn">
                    <p className="text-xs text-blue-400 flex items-center gap-1">
                      Click to view full details <MessageSquare className="w-3 h-3" />
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Activity className="w-12 h-12 mb-3 opacity-20" />
                <p>No recent activity found</p>
              </div>
            )}
          </div>
        </div>

        {/* Contact Queries */}
        <div className="admin-card">
          <div className="border-b border-primary pb-4 mb-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-accent">Contact Queries</h2>
              <div className="flex items-center gap-2">
                <span className="hidden md:inline text-sm text-gray-400">Messages submitted from</span>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-300 border border-blue-500/20">Contact Us</span>
              </div>
            </div>
          </div>
          {loadingQueries ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-400"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {contactQueries.map((query) => {
                const purpose = classifyPurpose(query.subject, query.message);
                return (
                  <div key={query._id} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700/50">
                    <div className="flex justify-between items-start gap-4">
                      <div className="min-w-0">
                        {/* From */}
                        <p className="text-[11px] uppercase tracking-wider text-gray-400">From</p>
                        <p className="text-white font-semibold mt-0.5 flex flex-wrap items-center gap-2 break-words">
                          <Users className="w-4 h-4 text-yellow-400" />
                          <span>{query.name || 'Unknown'}</span>
                          <span className="text-gray-400">({query.email || 'No email'})</span>
                          {query.email && (
                            <a href={`mailto:${query.email}`} className="text-yellow-400 text-xs hover:underline">Email</a>
                          )}
                        </p>

                        {/* Subject + Purpose */}
                        <p className="text-[11px] uppercase tracking-wider text-gray-400 mt-3">Subject</p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2">
                          <p className="text-gray-300">{query.subject || '—'}</p>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${purposeBadgeClass(purpose)}`}>
                            <Tag className="w-3 h-3" />
                            {purpose}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-300 border border-blue-500/20">Contact Us</span>
                        </div>

                        {/* Message */}
                        <p className="text-[11px] uppercase tracking-wider text-gray-400 mt-3">Message</p>
                        <p className="text-gray-200 mt-0.5 whitespace-pre-wrap break-words">{query.message}</p>
                      </div>
                      <div className="text-right shrink-0 flex flex-col items-end gap-2">
                        <p className="text-gray-500 text-sm">{new Date(query.createdAt).toLocaleString()}</p>
                        <button
                          onClick={() => handleDeleteQuery(query._id)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Delete Query"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;